import React, { useEffect } from 'react';
import { Modal } from 'react-bootstrap';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { Package, Wrench } from 'lucide-react';
import BrandSelect from '../../../Common/BrandSelect';
import '../../../Common/ll-modal.css';

const taxRateOptions = [
  { value: '0', label: '0%' },
  { value: '6', label: '6%' },
  { value: '12', label: '12%' },
  { value: '25', label: '25%' }
];

const unitOptions = [
  { value: 'Piece', label: 'Styck' },
  { value: 'Hour', label: 'Timmar' },
  { value: 'Liter', label: 'Liter' },
  { value: 'Kilogram', label: 'Kilogram' },
  { value: 'Square Meter', label: 'Kvadratmeter' },
  { value: 'Cubic Meter', label: 'Kubikmeter' },
  { value: 'Meter', label: 'Meter' },
  { value: 'Package', label: 'Förpackning' },
  { value: 'Set', label: 'Set' },
  { value: 'Day', label: 'Dag' },
  { value: 'Month', label: 'Månad' }
];

// Enhanced Yup Validation Schema
const validationSchema = Yup.object().shape({
  productcode: Yup.string()
    .min(2, 'Produktkod måste vara minst 2 tecken')
    .required('Produktkod är obligatorisk'),
  description: Yup.string()
    .min(3, 'Beskrivning måste vara minst 3 tecken')
    .required('Beskrivning är obligatorisk'),
  unitprice: Yup.number()
    .typeError('Pris måste vara ett giltigt nummer')
    .positive('Pris måste vara positivt')
    .required('Pris är obligatoriskt'),
  taxrate: Yup.string().required('Moms är obligatorisk'),
  unit: Yup.string().required('Enhet är obligatorisk'),
  type: Yup.string().required('Typ är obligatorisk')
});

const NewArticleModal = ({ show, handleClose, handleSave, handleUpdate, articleDetails }) => {
  const isEditMode = articleDetails !== null;

  const formik = useFormik({
    initialValues: {
      productcode: '',
      description: '',
      unitprice: '',
      type: 'Goods',
      unit: 'Piece',
      taxrate: '25'
    },
    validationSchema: validationSchema,
    onSubmit: (values) => {
      const payload = {
        productcode: values.productcode,
        description: values.description,
        unitprice: parseFloat(values.unitprice),
        type: values.type,
        unit: values.unit,
        taxrate: parseInt(values.taxrate)
      };

      if (isEditMode && handleUpdate) {
        handleUpdate(articleDetails.id, payload);
      } else {
        handleSave(payload);
      }
    }
  });

  const { values, errors, touched, setFieldValue, handleChange, handleBlur, handleSubmit, resetForm } = formik;

  useEffect(() => {
    if (articleDetails && show) {
      resetForm({
        values: {
          productcode: articleDetails.productcode || '',
          description: articleDetails.description || '',
          unitprice: articleDetails.unitprice || '',
          type: articleDetails.type || 'Goods',
          unit: articleDetails.unit || 'Piece',
          taxrate: String(articleDetails.tax_rate || 25)
        }
      });
    } else if (show && !articleDetails) {
      resetForm({
        values: {
          productcode: '',
          description: '',
          unitprice: '',
          type: 'Goods',
          unit: 'Piece',
          taxrate: '25'
        }
      });
    }
  }, [articleDetails, show, resetForm]);

  const handleCloseModal = () => {
    resetForm();
    handleClose();
  };

  const fieldError = (name) => (touched[name] && errors[name] ? errors[name] : null);
  const price = parseFloat(values.unitprice);
  const priceInclVat = price > 0 ? price * (1 + parseInt(values.taxrate, 10) / 100) : null;
  const formatKr = (v) => new Intl.NumberFormat('sv-SE', { style: 'currency', currency: 'SEK' }).format(v);

  return (
    <Modal show={show} onHide={handleCloseModal} backdrop="static" centered className="ll-modal">
      <form onSubmit={handleSubmit} noValidate>
        <Modal.Header closeButton>
          <Modal.Title>{isEditMode ? 'Redigera artikel' : 'Ny artikel'}</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <div className="ll-field">
            <label id="article-type-label">Typ</label>
            <div className="ll-segment" role="group" aria-labelledby="article-type-label">
              <button
                type="button"
                className={values.type === 'Goods' ? 'active' : ''}
                aria-pressed={values.type === 'Goods'}
                onClick={() => setFieldValue('type', 'Goods')}
              >
                <Package size={14} />
                Vara
              </button>
              <button
                type="button"
                className={values.type === 'Service' ? 'active' : ''}
                aria-pressed={values.type === 'Service'}
                onClick={() => setFieldValue('type', 'Service')}
              >
                <Wrench size={14} />
                Tjänst
              </button>
            </div>
          </div>

          <div className="ll-field__row">
            <div className="ll-field">
              <label htmlFor="article-code">Produktkod<span className="ll-required"> *</span></label>
              <input
                id="article-code"
                type="text"
                name="productcode"
                className={`ll-input${fieldError('productcode') ? ' is-invalid' : ''}`}
                value={values.productcode}
                onChange={handleChange}
                onBlur={handleBlur}
                autoFocus={!isEditMode}
              />
              {fieldError('productcode') && <span className="ll-field__hint is-error">{fieldError('productcode')}</span>}
            </div>

            <div className="ll-field">
              <label htmlFor="article-unit">Enhet<span className="ll-required"> *</span></label>
              <BrandSelect
                inputId="article-unit"
                name="unit"
                options={unitOptions}
                value={values.unit}
                onChange={(e) => setFieldValue('unit', e.target.value)}
                isInvalid={Boolean(fieldError('unit'))}
                isSearchable
              />
              {fieldError('unit') && <span className="ll-field__hint is-error">{fieldError('unit')}</span>}
            </div>
          </div>

          <div className="ll-field">
            <label htmlFor="article-description">Beskrivning<span className="ll-required"> *</span></label>
            <input
              id="article-description"
              type="text"
              name="description"
              className={`ll-input${fieldError('description') ? ' is-invalid' : ''}`}
              value={values.description}
              onChange={handleChange}
              onBlur={handleBlur}
              placeholder="Visas som radtext på fakturan"
            />
            {fieldError('description') && <span className="ll-field__hint is-error">{fieldError('description')}</span>}
          </div>

          <div className="ll-field__row">
            <div className="ll-field">
              <label htmlFor="article-price">Pris exkl. moms<span className="ll-required"> *</span></label>
              <div className="ll-input-unit">
                <input
                  id="article-price"
                  type="number"
                  name="unitprice"
                  inputMode="decimal"
                  className={`ll-input${fieldError('unitprice') ? ' is-invalid' : ''}`}
                  value={values.unitprice}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  step="0.01"
                  min="0"
                />
                <span>kr</span>
              </div>
              {fieldError('unitprice')
                ? <span className="ll-field__hint is-error">{fieldError('unitprice')}</span>
                : priceInclVat !== null && <span className="ll-field__hint">{formatKr(priceInclVat)} inkl. moms</span>}
            </div>

            <div className="ll-field">
              <label htmlFor="article-vat">Moms<span className="ll-required"> *</span></label>
              <BrandSelect
                inputId="article-vat"
                name="taxrate"
                options={taxRateOptions}
                value={values.taxrate}
                onChange={(e) => setFieldValue('taxrate', e.target.value)}
                isInvalid={Boolean(fieldError('taxrate'))}
              />
              {fieldError('taxrate') && <span className="ll-field__hint is-error">{fieldError('taxrate')}</span>}
            </div>
          </div>
        </Modal.Body>

        <Modal.Footer>
          <span className="ll-modal__note" />
          <div className="ll-modal__actions">
            <button type="button" className="ll-btn ll-btn--secondary" onClick={handleCloseModal}>
              Avbryt
            </button>
            <button type="submit" className="ll-btn ll-btn--primary">
              {isEditMode ? 'Spara ändringar' : 'Skapa artikel'}
            </button>
          </div>
        </Modal.Footer>
      </form>
    </Modal>
  );
};

export default NewArticleModal;
